import LazyLoad from "react-lazy-load";
import { useProductData } from "@/zustand/productStore.js";
import { useCartData } from "@/zustand/cartStore.js";
import { useCalculateProductCardPrice } from "@/func/orderCalculations.js";
import TagIcon from "@/utils/images/tagIcon";
import DiscountTagBlock from "@/components/OrderSummary/DiscountTagBlock";
import parse from "html-react-parser";
import glideImage from "@/func/glideImage.js";
import s from "@/components/ProductCards/ProductCard.module.scss";

const ProductCardNew = ({ product, modifications, index, type }) => {
  const { selectedProduct, warrantyState, discountCode } = useCartData();
  const { price, oldPrice, promoPrice } = useCalculateProductCardPrice(selectedProduct);
  const productData = useProductData();
  const warrantyLabel = window.post_template.checkout_v6_products.tr_checkout_v6_products_warrantyLabel;
  const discountAppliedLabel = window.post_template?.checkout_v6_summary?.tr_checkout_v6_discountApplied;
  const isBundleDesign = productData.show_bundle_design;

  // additional data extending selected product
  const extendedProduct = productData?.products?.find((obj) => obj.price == product?.price);
  const productImg = type === "fake" ? product?.image?.url : extendedProduct?.image?.url;
  const bundleImage = extendedProduct?.order_summary_bundle_image;
  const bundleImageFallback = bundleImage || productImg;
  const bundleText = extendedProduct?.tr_bundle_products;
  const additionalText = extendedProduct?.tr_additional_texts;
  const productSloganLabel = window?.post_template?.checkout_v6_products?.tr_checkout_v6_products_slogan_label;

  const productImage = glideImage(isBundleDesign ? bundleImageFallback : productImg, { w: 64, h: 64, fit: "crop" });

  const ProductPriceBlock = (oldPrice, price, promoPrice) => {
    const formattedPrice = productData?.formatted_string?.replace(/(0\.00|0,00|0)/, price);
    const formattedPromoPrice = productData?.formatted_string?.replace(/(0\.00|0,00|0)/, promoPrice);
    return !discountCode ? (
      <div className={s.productCard__priceBlock}>
        {oldPrice && <span className={s["productCard__priceBlock--oldPrice"]}>{oldPrice}</span>}
        {formattedPrice && <span className={s["productCard__priceBlock--price"]}>{formattedPrice}</span>}
      </div>
    ) : (
      <div className="productCard__priceBlock">
        {oldPrice && <span className={s["productCard__priceBlock--oldPrice"]}>{oldPrice}</span>}
        {formattedPromoPrice && <span className={s["productCard__priceBlock--price"]}>{formattedPromoPrice}</span>}
      </div>
    );
  };

  const fakeProductPrice =
    productData?.formatted_string && productData?.formatted_string.replace(/(0\.00|0,00|0)/, product.price);

  const fakeProductPriceBlock = () => {
    return (
      <div className={s.productCard__priceBlock}>
        <span className={s["productCard__priceBlock--oldPrice"]}>{fakeProductPrice}</span>
        <span className={s["productCard__priceBlock--price"]}>{product.price_line_label}</span>
      </div>
    );
  };
  return (
    <div
      key={index}
      className={`${s.productCard} ${modifications?.DrawerProducts ? s["productCard__smallMargin"] : ""}`}
    >
      <div className={s.productCard__imageContainer} style={{ height: "64px" }}>
        {productImg ? (
          <LazyLoad height={64} width={64}>
            <img
              src={productImage}
              alt={general_settings.brand_name}
              style={{ height: "64px" }}
              height="64"
              width="64"
              className="mon-img-64-w mon-img-64-h"
            />
          </LazyLoad>
        ) : (
          <div className="skeleton" style={{ height: "64px", width: "64px" }}></div>
        )}
        {!isBundleDesign && (
          <span data-cy="summary-product-amount" className={s["productCard__imageContainer--indicator"]}>
            {product.amount}
          </span>
        )}
      </div>
      <div className={s.productCard__titleBlock}>
        <div data-cy="summary-product-title" className={s["productCard__titleBlock--title"]}>
          {isBundleDesign && <span>{product?.amount}x </span>}
          {product.display_name} {type !== "fake" && warrantyState && <span>{warrantyLabel}</span>}
        </div>
        {isBundleDesign && (
          <div data-cy="summary-product-subtitle" className={s["productCard__titleBlock--subtitle"]}>
            {product.tr_bundle_products}
          </div>
        )}
        <div className={s["productCard__titleBlock--productTitle"]}>{productSloganLabel}</div>
        {window?.post_template?.checkout_v6_products?.checkout_v6_products_discountAppliedToggle && (
          <DiscountTagBlock discountAppliedLabel={discountAppliedLabel} />
        )}
        {productData?.show_bundle_design && bundleText && (
          <div className={s["productCard__titleBlock--altText"]}>{parse(bundleText)}</div>
        )}
        {additionalText && <div className={s["productCard__titleBlock--altText"]}>{additionalText}</div>}
        {type == "fake" ? (
          <>
            {product.tag_label && (
              <div className={s["productCard__titleBlock--tagText"]}>
                <TagIcon />
                {product.tag_label} {`(-${fakeProductPrice})`}
              </div>
            )}
          </>
        ) : (
          <>
            {discountCode && (
              <DiscountTag
                price={price}
                promoPrice={promoPrice}
                discountCode={discountCode}
                formattedString={productData.formatted_string}
              />
            )}
          </>
        )}
      </div>
      {type !== "fake" ? ProductPriceBlock(oldPrice, price, promoPrice) : fakeProductPriceBlock()}
    </div>
  );
};

function DiscountTag({ price, promoPrice, discountCode, formattedString }) {
  const additionalSavings = (Number(price) - Number(promoPrice)).toFixed(2);
  const additionalSavingsFormatted = formattedString?.replace(/(0\.00|0,00|0)/, additionalSavings);

  return (
    <div className={s["productCard__titleBlock--tagText"]}>
      <TagIcon />
      {discountCode} {`(-${additionalSavingsFormatted})`}
    </div>
  );
}

export default ProductCardNew;
